import * as React from 'react';
import { Link } from 'react-router';
import { Section, useCacheQuery } from 'gossamer-front-end';
import { EventProfile } from 'cb-model';
import { EventProfileCard } from '../../event-profile/EventProfileCard';
import { EventDetailsSectionLayout } from './EventDetailsSectionLayout';
import { EventDetails, HandleEventDetailsChange } from './eventDetailsFormTypes';
import { applySelectedEventProfile, findSelectedEventProfile, parseOptionalInt } from './eventProfileUtils';

type Props<T extends EventDetails> = {
  eventDetails: T | undefined;
  handleDetailsChange: HandleEventDetailsChange<T>;
  institutionId: number;
};

export function EventProfileSection<T extends EventDetails>(props: Props<T>): React.JSX.Element {
  const { eventDetails, handleDetailsChange, institutionId } = props;

  const eventProfileQuery = useCacheQuery(EventProfile, {
    where: {
      and: [
        { fieldId: 'institution_id', operator: 'eq', value: institutionId },
        { fieldId: 'status', operator: 'eq', value: 'ACTIVE' },
      ],
    },
    order: [['seq', 'ASC']],
    limit: 100,
  });

  const profiles = (eventProfileQuery.data || []) as any[];
  const selectedProfileId = (eventDetails as any)?.event_profile_id;

  const handleSelect = (profileId: string) => {
    handleDetailsChange('event_profile_id' as keyof T, parseOptionalInt(profileId));
    applySelectedEventProfile({
      selectedProfile: findSelectedEventProfile(profiles, profileId),
      handleDetailsChange,
    });
  };

  return (
    <Section style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexFlow: 'column nowrap' }}>
      <div>
        <h2 style={{ margin: 0, marginBottom: 8, fontSize: 20 }}>Event Profile</h2>
        <p style={{ margin: 0 }}>
          Select an event profile to control visibility, post-event flow, and default settings for this event.
        </p>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
        {profiles.map((profile) => (
          <EventProfileCard
            key={profile.id}
            profile={profile}
            selected={selectedProfileId != null && String(selectedProfileId) === String(profile.id)}
            onClick={() => handleSelect(String(profile.id))}
          />
        ))}
        {eventProfileQuery.isFetching && profiles.length === 0 && <span>Loading profiles…</span>}
        {!eventProfileQuery.isFetching && profiles.length === 0 && (
          <span className="text-muted">No event profiles found.</span>
        )}
      </div>
      <div style={{ fontSize: '0.875rem' }}>
        To customise feedback forms or certificates,{' '}
        <Link to="/admin/event-profile/search">manage event profiles →</Link>
      </div>
    </Section>
  );
}
